import type { ReverseCalculationInput, ReverseCalculationResult, ReverseCalculationInputWithYear } from '../../types/tax';
import { calculateTax } from './calculateTax';

const MAX_ITERATIONS = 100;
const TOLERANCE = 1; // Within R1 accuracy

export function calculateReverseIncome(input: ReverseCalculationInput | ReverseCalculationInputWithYear): ReverseCalculationResult {
  const { desiredNetIncome, ageCategory, advancedOptions } = input;
  const taxYear = (input as ReverseCalculationInputWithYear).taxYear;
  
  // Set reasonable bounds for binary search
  let lowerBound = desiredNetIncome; // Minimum possible gross (when no tax)
  let upperBound = desiredNetIncome * 3; // Conservative upper bound (assuming max ~66% tax rate)
  
  let iterations = 0;
  let bestResult = null;
  let closestDifference = Infinity;
  
  // Binary search to find the gross income that yields desired net income
  while (iterations < MAX_ITERATIONS && upperBound - lowerBound > TOLERANCE) {
    iterations++;
    
    const midPoint = (lowerBound + upperBound) / 2;
    
    // Calculate tax for this gross income, passing taxYear if specified
    const taxResult = calculateTax({
      grossIncome: midPoint,
      ageCategory,
      advancedOptions,
      ...(taxYear ? { taxYear } : {})
    } as Parameters<typeof calculateTax>[0]);
    
    const difference = Math.abs(taxResult.netIncome - desiredNetIncome);
    
    // Track the best result found so far
    if (difference < closestDifference) {
      closestDifference = difference;
      bestResult = {
        ...taxResult,
        iterations,
        accuracy: difference
      };
    }
    
    // Adjust bounds based on comparison
    if (taxResult.netIncome < desiredNetIncome) {
      // Net income too low, need higher gross income
      lowerBound = midPoint;
    } else {
      // Net income too high, need lower gross income
      upperBound = midPoint;
    }
    
    // Early exit if we're within tolerance
    if (difference <= TOLERANCE) {
      break;
    }
  }
  
  // If we didn't find a good result, use the best one we found
  if (!bestResult) {
    const fallbackGross = desiredNetIncome * 1.5; // Fallback estimate
    const fallbackResult = calculateTax({
      grossIncome: fallbackGross,
      ageCategory,
      advancedOptions,
      ...(taxYear ? { taxYear } : {})
    } as Parameters<typeof calculateTax>[0]);
    
    bestResult = {
      ...fallbackResult,
      iterations,
      accuracy: Math.abs(fallbackResult.netIncome - desiredNetIncome)
    };
  }
  
  return bestResult;
}

export function calculateReverseIncomeWithBounds(
  input: ReverseCalculationInput | ReverseCalculationInputWithYear,
  customLowerBound?: number,
  customUpperBound?: number
): ReverseCalculationResult {
  const { desiredNetIncome, ageCategory, advancedOptions } = input;
  const taxYear = (input as ReverseCalculationInputWithYear).taxYear;
  
  // Use custom bounds if provided
  let lowerBound = customLowerBound ?? desiredNetIncome;
  let upperBound = customUpperBound ?? desiredNetIncome * 3;
  
  let iterations = 0;
  let bestResult = null;
  let closestDifference = Infinity;
  
  while (iterations < MAX_ITERATIONS && upperBound - lowerBound > TOLERANCE) {
    iterations++;
    
    const midPoint = (lowerBound + upperBound) / 2;
    
    const taxResult = calculateTax({
      grossIncome: midPoint,
      ageCategory,
      advancedOptions,
      ...(taxYear ? { taxYear } : {})
    } as Parameters<typeof calculateTax>[0]);
    
    const difference = Math.abs(taxResult.netIncome - desiredNetIncome);
    
    if (difference < closestDifference) {
      closestDifference = difference;
      bestResult = {
        ...taxResult,
        iterations,
        accuracy: difference
      };
    }
    
    if (taxResult.netIncome < desiredNetIncome) {
      lowerBound = midPoint;
    } else {
      upperBound = midPoint;
    }
    
    if (difference <= TOLERANCE) {
      break;
    }
  }
  
  if (!bestResult) {
    const fallbackGross = (lowerBound + upperBound) / 2;
    const fallbackResult = calculateTax({
      grossIncome: fallbackGross,
      ageCategory,
      advancedOptions,
      ...(taxYear ? { taxYear } : {})
    } as Parameters<typeof calculateTax>[0]);
    
    bestResult = {
      ...fallbackResult,
      iterations,
      accuracy: Math.abs(fallbackResult.netIncome - desiredNetIncome)
    };
  }
  
  return bestResult;
}

export function estimateGrossIncome(desiredNetIncome: number): number {
  // Quick estimation function for initial bounds
  // This is a rough approximation and will be refined by the binary search
  
  // Assume average effective tax rate based on income level
  let estimatedTaxRate = 0.18; // Start with lowest bracket
  
  if (desiredNetIncome > 200000) estimatedTaxRate = 0.25;
  if (desiredNetIncome > 400000) estimatedTaxRate = 0.30;
  if (desiredNetIncome > 600000) estimatedTaxRate = 0.35;
  if (desiredNetIncome > 800000) estimatedTaxRate = 0.40;
  
  // Add UIF contribution (~1%)
  const estimatedTotalDeductionRate = estimatedTaxRate + 0.01;
  
  // Gross = Net / (1 - total_deduction_rate)
  return desiredNetIncome / (1 - estimatedTotalDeductionRate);
}